import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, Trash2, Grid3X3, List, Save, Edit, CheckCircle, BookmarkCheck } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { TransactionFormDialog } from '@/components/dialogs/TransactionFormDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Transactions = ({ user }) => {
  const { settings } = useSettings();
  const { isMobile } = useScreenSize();
  const currencySymbol = settings.currency_symbol || 'DT';
  const itemsPerPage = useMemo(() => settings.items_per_page || 10, [settings.items_per_page]);
  
  const [transactions, setTransactions] = useState([]);
  const [sheep, setSheep] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showQuickSheepForm, setShowQuickSheepForm] = useState(false);
  
  // Finalize dialog
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeTransaction, setFinalizeTransaction] = useState(null);
  const [finalizeData, setFinalizeData] = useState({
    prix_total: '',
    reste_a_payer: '',
    date_finalisation: new Date().toISOString().split('T')[0],
  });
  
  // Filter/sort/pagination
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterType, setFilterType] = useState('tous');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  
  // Reservation toggle
  const [isReservation, setIsReservation] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'vente',
    sheep_id: '',
    date: new Date().toISOString().split('T')[0],
    prix: '',
    contact: '',
    notes: '',
    acompte: '',
  });
  const [quickSheepData, setQuickSheepData] = useState({
    numero: '',
    nom: '',
    sexe: 'femelle',
    race: '',
  });
  const [autoSheepNumber, setAutoSheepNumber] = useState(true);

  const canWrite = user?.permissions?.includes('write') || user?.role === 'admin';
  const canDelete = user?.permissions?.includes('delete') || user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isMobile && viewMode === 'table') {
      setViewMode('cards');
    }
  }, [isMobile, viewMode]);

  useEffect(() => {
    if (showQuickSheepForm && autoSheepNumber) {
      fetchNextSheepNumber();
    }
  }, [showQuickSheepForm, autoSheepNumber]);

  // Filter & sort
  const filteredTransactions = useMemo(() => {
    let filtered = Array.isArray(transactions) ? [...transactions] : [];
    
    if (filterType === 'vente') {
      filtered = filtered.filter(tx => tx.type === 'vente' && !tx.is_reservation);
    } else if (filterType === 'achat') {
      filtered = filtered.filter(tx => tx.type === 'achat');
    } else if (filterType === 'reservation') {
      filtered = filtered.filter(tx => tx.is_reservation);
    }
    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc': return new Date(b.date) - new Date(a.date);
        case 'date-asc': return new Date(a.date) - new Date(b.date);
        case 'type': return a.type.localeCompare(b.type);
        case 'prix-desc': return b.prix - a.prix;
        case 'prix-asc': return a.prix - b.prix;
        default: return new Date(b.date) - new Date(a.date);
      }
    });
    
    return filtered;
  }, [transactions, sortBy, filterType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, filterType]);

  const fetchData = useCallback(async () => {
    try {
      const [txRes, sheepRes, breedsRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/sheep`),
        axios.get(`${API}/breeds`),
      ]);
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      setSheep(Array.isArray(sheepRes.data) ? sheepRes.data : []);
      setBreeds(Array.isArray(breedsRes.data) ? breedsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNextSheepNumber = async () => {
    if (!autoSheepNumber) return;
    try {
      const response = await axios.get(`${API}/sheep/next-number`);
      setQuickSheepData(prev => ({ ...prev, numero: response.data.next_number }));
    } catch (error) {
      console.error('Error fetching next sheep number:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      toast.error('Vous n\'avez pas la permission d\'effectuer cette action');
      return;
    }

    if (formData.type === 'vente' && !formData.sheep_id) {
      toast.error('Vous devez sélectionner un mouton pour une vente');
      return;
    }

    try {
      let sheepId = formData.sheep_id || null;
      
      if (formData.type === 'achat' && showQuickSheepForm && quickSheepData.numero) {
        if (!quickSheepData.race) {
          toast.error('Vous devez sélectionner une race pour le nouveau mouton');
          return;
        }
        const sheepResponse = await axios.post(`${API}/sheep`, {
          numero: quickSheepData.numero,
          nom: quickSheepData.nom,
          date_naissance: formData.date,
          race: quickSheepData.race,
          sexe: quickSheepData.sexe,
          statut: 'actif',
        });
        sheepId = sheepResponse.data.id;
        toast.success('Nouveau mouton créé avec succès');
      }

      const data = {
        ...formData,
        prix: parseFloat(formData.prix) || 0,
        sheep_id: sheepId,
        is_reservation: formData.type === 'vente' && isReservation,
        acompte: isReservation ? (parseFloat(formData.acompte) || 0) : null,
      };

      if (selectedTransaction) {
        await axios.put(`${API}/transactions/${selectedTransaction.id}`, data);
        toast.success('Transaction modifiée avec succès');
      } else {
        await axios.post(`${API}/transactions`, data);
        toast.success(isReservation ? 'Réservation enregistrée avec succès' : 'Transaction ajoutée avec succès');
      }

      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving transaction:', error);
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || 'Erreur de validation').join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleFinalize = async (e) => {
    e.preventDefault();
    if (!finalizeTransaction) return;
    
    try {
      await axios.post(`${API}/transactions/${finalizeTransaction.id}/finalize`, {
        reste_a_payer: parseFloat(finalizeData.reste_a_payer),
        date_finalisation: finalizeData.date_finalisation,
        prix_total: parseFloat(finalizeData.prix_total) || 0,
      });
      toast.success('Vente finalisée avec succès');
      fetchData();
      setFinalizeDialogOpen(false);
      setFinalizeTransaction(null);
    } catch (error) {
      console.error('Error finalizing:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de la finalisation';
      toast.error(msg);
    }
  };

  const openFinalizeDialog = (tx) => {
    setFinalizeTransaction(tx);
    const acompte = tx.acompte || 0;
    const prixTotal = tx.prix || 0;
    const reste = Math.max(0, prixTotal - acompte);
    setFinalizeData({
      prix_total: prixTotal.toString(),
      reste_a_payer: reste.toFixed(2),
      date_finalisation: new Date().toISOString().split('T')[0],
    });
    setFinalizeDialogOpen(true);
  };

  const openDialog = (transaction = null) => {
    if (transaction) {
      setSelectedTransaction(transaction);
      setFormData({
        type: transaction.type,
        sheep_id: transaction.sheep_id || '',
        date: transaction.date,
        prix: transaction.prix.toString(),
        contact: transaction.contact || '',
        notes: transaction.notes || '',
        acompte: transaction.acompte?.toString() || '',
      });
      setIsReservation(transaction.is_reservation || false);
      setShowQuickSheepForm(false);
    } else {
      setSelectedTransaction(null);
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'vente',
      sheep_id: '',
      date: new Date().toISOString().split('T')[0],
      prix: '',
      contact: '',
      notes: '',
      acompte: '',
    });
    setQuickSheepData({ numero: '', nom: '', sexe: 'femelle', race: '' });
    setShowQuickSheepForm(false);
    setAutoSheepNumber(true);
    setSelectedTransaction(null);
    setIsReservation(false);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      toast.error('Vous n\'avez pas la permission de supprimer');
      return;
    }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction ?')) {
      try {
        await axios.delete(`${API}/transactions/${id}`);
        toast.success('Transaction supprimée avec succès');
        fetchData();
      } catch (error) {
        console.error('Error deleting transaction:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const getSheepById = (id) => sheep.find((s) => s.id === id);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

  // Stats - ventes finalisées (pas les réservations en cours)
  const txList = Array.isArray(transactions) ? transactions : [];
  const ventesFinalisees = txList.filter(t => t.type === 'vente' && !t.is_reservation);
  const reservationsEnCours = txList.filter(t => t.is_reservation);
  const totalVentes = ventesFinalisees.reduce((sum, t) => sum + (t.prix || 0), 0);
  const totalAchats = txList.filter(t => t.type === 'achat').reduce((sum, t) => sum + (t.prix || 0), 0);
  const totalAcomptes = reservationsEnCours.reduce((sum, t) => sum + (t.acompte || 0), 0);
  const balance = totalVentes + totalAcomptes - totalAchats;

  // Helpers
  const getTxLabel = (tx) => {
    if (tx.is_reservation) return 'reservation';
    return tx.type;
  };

  const getTxBadge = (tx) => {
    if (tx.is_reservation) {
      return { label: '📌 Réservé', cls: 'bg-amber-100 text-amber-700' };
    }
    if (tx.type === 'vente') {
      return { label: '📈 Vente', cls: 'bg-green-100 text-green-700' };
    }
    return { label: '📉 Achat', cls: 'bg-blue-100 text-blue-700' };
  };

  // Table view
  const renderTableView = () => (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl">Tableau des transactions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200 dark:border-gray-800 dark:border-gray-800">
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Type</th>
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Date</th>
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden md:table-cell">Mouton</th>
                <th className="text-right py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Prix</th>
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden lg:table-cell">Contact</th>
                <th className="text-right py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucune transaction enregistrée
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map((tx) => {
                  const sheepData = tx.sheep_id ? getSheepById(tx.sheep_id) : null;
                  const badge = getTxBadge(tx);
                  return (
                    <tr key={tx.id} className={`border-b border-gray-100 hover:bg-gray-50 ${tx.is_reservation ? 'bg-amber-50' : ''}`}
                        data-testid={`tx-row-${tx.id}`}>
                      <td className="py-3 px-2 lg:px-4">
                        <span className={`px-2 py-1 lg:px-3 lg:py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-2 lg:px-4 text-gray-900 text-xs lg:text-sm">
                        {new Date(tx.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-2 lg:px-4 text-gray-600 text-xs lg:text-sm hidden md:table-cell">
                        {sheepData ? `N° ${sheepData.numero} ${sheepData.nom || ''}`.trim() : '-'}
                      </td>
                      <td className="py-3 px-2 lg:px-4 text-right text-xs lg:text-sm">
                        {tx.is_reservation ? (
                          <div>
                            <div className="font-semibold text-amber-700">{tx.prix.toFixed(2)} {currencySymbol}</div>
                            <div className="text-xs text-amber-600">Acompte: {(tx.acompte || 0).toFixed(2)} {currencySymbol}</div>
                          </div>
                        ) : (
                          <span className="font-semibold text-gray-900 dark:text-white">{tx.prix.toFixed(2)} {currencySymbol}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 lg:px-4 text-gray-600 text-xs lg:text-sm hidden lg:table-cell">
                        {tx.contact || '-'}
                      </td>
                      <td className="py-3 px-1 lg:px-4 text-right">
                        <div className="flex gap-1 justify-end">
                          {tx.is_reservation && canWrite && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openFinalizeDialog(tx)}
                              className="text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:bg-slate-900/30 p-1 lg:p-2"
                              title="Finaliser la vente"
                              data-testid={`finalize-btn-${tx.id}`}
                            >
                              <CheckCircle className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                          {canWrite && !tx.is_reservation && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDialog(tx)}
                              className="text-blue-600 hover:bg-blue-50 p-1 lg:p-2"
                              title="Modifier"
                            >
                              <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(tx.id)}
                              className="text-red-600 hover:bg-red-50 p-1 lg:p-2"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 dark:border-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">Page {currentPage} sur {totalPages}</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Précédent</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Suivant</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Cards view
  const renderCardsView = () => (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl">Transactions en cartes</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginatedTransactions.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
              <p>Aucune transaction enregistrée</p>
            </div>
          ) : (
            paginatedTransactions.map((tx) => {
              const sheepData = tx.sheep_id ? getSheepById(tx.sheep_id) : null;
              const badge = getTxBadge(tx);
              return (
                <Card key={tx.id} className={`border hover:shadow-lg transition-shadow ${tx.is_reservation ? 'border-l-4 border-l-amber-400 bg-amber-50/50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {new Date(tx.date).toLocaleDateString('fr-FR')}
                          </div>
                          {sheepData && (
                            <div className="text-sm text-gray-600 mt-1">
                              🐑 N° {sheepData.numero} {sheepData.nom || ''}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className={`text-xl font-bold ${
                            tx.is_reservation ? 'text-amber-600' : tx.type === 'vente' ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {tx.type === 'vente' ? '+' : '-'}{tx.prix.toFixed(2)} {currencySymbol}
                          </div>
                          {tx.is_reservation && (
                            <div className="text-xs text-amber-600 mt-1">
                              Acompte: {(tx.acompte || 0).toFixed(2)} {currencySymbol}
                            </div>
                          )}
                        </div>
                      </div>

                      {tx.contact && (
                        <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-950 p-2 rounded">
                          👤 {tx.contact}
                        </div>
                      )}

                      {tx.notes && (
                        <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-950 p-2 rounded">
                          📝 {tx.notes}
                        </div>
                      )}

                      {(canWrite || canDelete) && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex gap-2">
                            {tx.is_reservation && canWrite && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openFinalizeDialog(tx)}
                                className="flex-1 text-slate-700 dark:text-slate-300 border-gray-200 dark:border-gray-800 hover:bg-slate-50 dark:bg-slate-900/30"
                                data-testid={`finalize-card-btn-${tx.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Finaliser
                              </Button>
                            )}
                            {canWrite && !tx.is_reservation && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openDialog(tx)}
                                className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(tx.id)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 dark:border-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">Page {currentPage} sur {totalPages}</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Précédent</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Suivant</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Achats & Ventes</h1>
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <Button
              onClick={() => openDialog()}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
              data-testid="add-transaction-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une transaction
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Ventes</p>
                <p className="text-2xl font-bold text-green-600">{totalVentes.toFixed(2)} {currencySymbol}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <TrendingDown className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Achats</p>
                <p className="text-2xl font-bold text-blue-600">{totalAchats.toFixed(2)} {currencySymbol}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <BookmarkCheck className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Acomptes encaissés</p>
                <p className="text-2xl font-bold text-amber-600">{totalAcomptes.toFixed(2)} {currencySymbol}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-slate-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-slate-700 dark:text-slate-300" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bilan</p>
                <p className={`text-2xl font-bold ${balance >= 0 ? 'text-slate-700 dark:text-slate-300' : 'text-red-600'}`}>
                  {balance >= 0 ? '+' : ''}{balance.toFixed(2)} {currencySymbol}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Filtrer par type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">💼 Toutes les transactions</SelectItem>
                  <SelectItem value="vente">📈 Ventes uniquement</SelectItem>
                  <SelectItem value="achat">📉 Achats uniquement</SelectItem>
                  <SelectItem value="reservation">📌 Réservations en cours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Trier par</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">📅 Date (récente)</SelectItem>
                  <SelectItem value="date-asc">📅 Date (ancienne)</SelectItem>
                  <SelectItem value="type">💼 Type</SelectItem>
                  <SelectItem value="prix-desc">💰 Prix (↑)</SelectItem>
                  <SelectItem value="prix-asc">💰 Prix (↓)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Affichage</Label>
              <div className="flex gap-1">
                {!isMobile && (
                  <Button
                    type="button"
                    variant={viewMode === 'table' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('table')}
                    data-testid="table-view-button"
                    title="Vue tableau"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('cards')}
                  data-testid="cards-view-button"
                  title="Vue cartes"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Résultats</Label>
              <div className="h-10 flex items-center px-3 bg-gray-50 dark:bg-gray-950 rounded-lg border">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {filteredTransactions.length} transaction(s) affichée(s)
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data display */}
      {viewMode === 'table' && renderTableView()}
      {viewMode === 'cards' && renderCardsView()}

      {/* Add/Edit Transaction Dialog */}
      {/* Add/Edit Transaction Dialog */}
      <TransactionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedTransaction={selectedTransaction}
        formData={formData}
        setFormData={setFormData}
        isReservation={isReservation}
        setIsReservation={setIsReservation}
        showQuickSheepForm={showQuickSheepForm}
        setShowQuickSheepForm={setShowQuickSheepForm}
        quickSheepData={quickSheepData}
        setQuickSheepData={setQuickSheepData}
        autoSheepNumber={autoSheepNumber}
        setAutoSheepNumber={setAutoSheepNumber}
        onFetchNextSheepNumber={fetchNextSheepNumber}
        onSubmit={handleSubmit}
        sheep={sheep}
        breeds={breeds}
        currencySymbol={currencySymbol}
      />


      {/* Finalize Reservation Dialog */}
      <Dialog open={finalizeDialogOpen} onOpenChange={setFinalizeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Finaliser la vente</DialogTitle>
            <DialogDescription>
              Complétez la vente pour{' '}
              {finalizeTransaction?.sheep_id && getSheepById(finalizeTransaction.sheep_id) 
                ? `N° ${getSheepById(finalizeTransaction.sheep_id).numero} ${getSheepById(finalizeTransaction.sheep_id).nom || ''}`
                : 'ce mouton'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFinalize} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Acompte versé:</span>
                <span className="font-semibold text-amber-700">{(finalizeTransaction?.acompte || 0).toFixed(2)} {currencySymbol}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finalize_prix_total">Prix total final * ({currencySymbol})</Label>
              <Input
                id="finalize_prix_total"
                type="number"
                step="0.01"
                min="0"
                value={finalizeData.prix_total}
                onChange={(e) => {
                  const newPrix = e.target.value;
                  const acompte = finalizeTransaction?.acompte || 0;
                  const reste = Math.max(0, (parseFloat(newPrix) || 0) - acompte);
                  setFinalizeData({ ...finalizeData, prix_total: newPrix, reste_a_payer: reste.toFixed(2) });
                }}
                required
                data-testid="finalize-prix-total-input"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">Vous pouvez ajuster le prix final ici</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reste_a_payer">Reste à payer ({currencySymbol})</Label>
              <Input
                id="reste_a_payer"
                type="number"
                step="0.01"
                min="0"
                value={finalizeData.reste_a_payer}
                onChange={(e) => setFinalizeData({ ...finalizeData, reste_a_payer: e.target.value })}
                required
                data-testid="finalize-reste-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_finalisation">Date de finalisation *</Label>
              <Input
                id="date_finalisation"
                type="date"
                value={finalizeData.date_finalisation}
                onChange={(e) => setFinalizeData({ ...finalizeData, date_finalisation: e.target.value })}
                required
                data-testid="finalize-date-input"
              />
            </div>

            <div className="bg-slate-50 dark:bg-slate-900/30 border border-gray-200 dark:border-gray-800 rounded-lg p-3">
              <div className="flex justify-between text-sm font-bold">
                <span>Total final:</span>
                <span className="text-slate-800 dark:text-slate-200">{parseFloat(finalizeData.prix_total || 0).toFixed(2)} {currencySymbol}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Acompte {(finalizeTransaction?.acompte || 0).toFixed(2)} + Reste {parseFloat(finalizeData.reste_a_payer || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                data-testid="finalize-submit-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer la vente
              </Button>
              <Button type="button" variant="outline" onClick={() => setFinalizeDialogOpen(false)}>
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
